import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  CustomerApplicationFieldSourceState,
  CustomerApplicationFieldValueContract,
  QuestionnaireSourceAnswerDetail,
} from '@/contracts/customer-application-binding'
import { customerFieldTargetOptions } from '@/contracts/customer-application-binding'
import type { QuestionnaireAnswer } from '@/lib/business-workflow'

export type ErpFieldMappingRow = {
  id: string
  targetField?: string
  targetLabel: string
  dbFieldName?: string
  dataType?: string
  sourceQuestionTitle?: string
  sourceQuestionId?: string
  value: string
  sourceState?: CustomerApplicationFieldSourceState
}

const targetOptionsByValue = new Map<string, (typeof customerFieldTargetOptions)[number]>(
  customerFieldTargetOptions.map((option) => [option.value, option]),
)

const sourceStateLabel: Record<CustomerApplicationFieldSourceState, string> = {
  prefilled_from_question: '問卷帶入',
  manual: '手動欄位',
  overridden: '已人工變更',
  missing_source: '缺少來源',
}

function targetMetadata(targetField: string | undefined) {
  return targetField ? targetOptionsByValue.get(targetField) : undefined
}

function fieldTargetLabel(targetField: string | undefined, fallback: string | undefined) {
  const option = targetMetadata(targetField)
  return option?.label ?? fallback ?? '未綁定 ERP 欄位'
}

function fieldDbName(targetField: string | undefined, fallback: string | undefined) {
  return fallback ?? targetMetadata(targetField)?.dbFieldName
}

function fieldDataType(targetField: string | undefined, fallback: string | undefined) {
  return fallback ?? targetMetadata(targetField)?.dataType
}

export function buildErpRowsFromFieldValues(
  values: CustomerApplicationFieldValueContract[],
): ErpFieldMappingRow[] {
  return values.map((fieldValue) => ({
    id: `field-${fieldValue.fieldKey}`,
    targetField: fieldValue.fieldKey,
    targetLabel: fieldTargetLabel(fieldValue.fieldKey, fieldValue.targetLabel ?? fieldValue.label),
    dbFieldName: fieldDbName(fieldValue.fieldKey, fieldValue.dbFieldName),
    dataType: fieldDataType(fieldValue.fieldKey, fieldValue.dataType),
    sourceQuestionTitle: fieldValue.sourceQuestionTitle,
    sourceQuestionId: fieldValue.sourceQuestionId,
    value: fieldValue.value,
    sourceState: fieldValue.sourceState,
  }))
}

export function buildErpRowsFromSourceAnswers(
  answers: QuestionnaireSourceAnswerDetail[],
): ErpFieldMappingRow[] {
  return answers.map((answer) => ({
    id: `answer-${answer.questionId}`,
    targetField: answer.mappedTarget,
    targetLabel: fieldTargetLabel(
      answer.mappedTarget,
      answer.mappedTargetFullLabel ?? answer.mappedTargetLabel,
    ),
    dbFieldName: fieldDbName(answer.mappedTarget, answer.mappedDbFieldName),
    dataType: fieldDataType(answer.mappedTarget, answer.mappedDataType),
    sourceQuestionTitle: answer.questionTitle,
    sourceQuestionId: answer.questionId,
    value: answer.valuePreview,
    sourceState: answer.sourceState,
  }))
}

export function buildErpRowsFromQuestionnaireAnswers(
  answers: QuestionnaireAnswer[],
): ErpFieldMappingRow[] {
  return answers.map((answer) => ({
    id: `questionnaire-answer-${answer.id}`,
    targetField: answer.mappedField,
    targetLabel: fieldTargetLabel(answer.mappedField, answer.mappedField),
    dbFieldName: fieldDbName(answer.mappedField, undefined),
    dataType: fieldDataType(answer.mappedField, undefined),
    sourceQuestionTitle: answer.questionTitle,
    value: answer.answer,
    sourceState: 'prefilled_from_question',
  }))
}

export function ErpFieldMappingTable({
  rows,
  title = 'ERP 客戶欄位對照',
  description = '列出問卷答案或申請欄位會對應到哪個 ERP 客戶欄位與 DB 欄位。',
  emptyMessage = '目前沒有可顯示的 ERP 欄位對照。',
  showHeader = true,
}: {
  rows: ErpFieldMappingRow[]
  title?: string
  description?: string
  emptyMessage?: string
  showHeader?: boolean
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
        {emptyMessage}
      </div>
    )
  }

  return (
    <section className="space-y-3">
      {showHeader ? (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-slate-950">{title}</h3>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>
          <Badge variant="outline">ERP 欄位 {rows.length}</Badge>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <Table aria-label={title}>
          <TableHeader>
            <TableRow>
              <TableHead>ERP 客戶欄位</TableHead>
              <TableHead>DB 欄位</TableHead>
              <TableHead>來源題目</TableHead>
              <TableHead>問卷答案 / 帶入值</TableHead>
              <TableHead>狀態</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="min-w-[220px] whitespace-normal break-words font-medium text-slate-900">
                  {row.targetLabel}
                </TableCell>
                <TableCell className="min-w-[180px]">
                  <div className="font-mono text-sm text-slate-900">
                    {row.dbFieldName ?? '未設定'}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{row.dataType ?? '未設定'}</div>
                </TableCell>
                <TableCell className="min-w-[220px] whitespace-normal break-words">
                  <div className="text-slate-900">{row.sourceQuestionTitle ?? '手動欄位'}</div>
                  {row.sourceQuestionId ? (
                    <div className="mt-1 font-mono text-xs text-slate-500">
                      {row.sourceQuestionId}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="min-w-[220px] max-w-[360px] whitespace-normal break-words">
                  {row.value || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={row.targetField ? 'secondary' : 'outline'}>
                    {row.sourceState ? sourceStateLabel[row.sourceState] : '未設定'}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
